import { isDTS } from "./platform";

/** @typedef {'nfl' | 'collegeFootball' | 'nba' | 'collegeBasketball'} TeaserChartKey */

export const TEASER_CHART_LABELS = {
  nfl: "NFL",
  collegeFootball: "College Football",
  nba: "NBA",
  collegeBasketball: "College Basketball",
};

/**
 * Client teaser charts — sourced from Wager Examples.xlsx "Teaser Chart" tab.
 * Standard football points (6–9) plus NFL Special (10–13) merged per team count.
 */
export const NFL_TEASER_ODDS_MAP = {
  2: { 6: -120, 6.5: -130, 7: -140, 8: -165, 9: -185 },
  3: { 6: 150, 6.5: 135, 7: 120, 8: 105, 9: -115, 10: -130, 11: -155 },
  4: { 6: 260, 6.5: 225, 7: 200, 8: 155, 9: 130, 10: 110, 11: -105, 12: -125, 13: -140 },
  5: { 6: 400, 6.5: 350, 7: 325, 8: 225, 9: 185, 10: 150, 11: 130, 12: 110, 13: -105 },
  6: { 6: 600, 6.5: 500, 7: 450, 8: 300, 9: 250, 10: 200, 11: 170, 12: 130, 13: 120 },
  7: { 6: 900, 6.5: 800, 7: 700, 8: 425, 9: 325, 10: 275, 11: 220, 12: 180, 13: 150 },
  8: { 6: 1400, 6.5: 1100, 7: 900, 8: 600, 9: 425, 10: 350, 11: 275, 12: 325, 13: 190 },
  9: { 6: 1900, 6.5: 1500, 7: 1200, 8: 750, 9: 550, 10: 450, 11: 350, 12: 275, 13: 225 },
  10: { 6: 2500, 6.5: 2000, 7: 1500, 8: 1000, 9: 700, 10: 550, 11: 425, 12: 325, 13: 275 },
  11: { 7: 1800, 8: 1300, 9: 900, 10: 700, 11: 525, 12: 400, 13: 350 },
  12: { 8: 1600, 9: 1200, 10: 900, 11: 650, 12: 500, 13: 400 },
};

/** College football chart — different payouts from NFL (client xlsx). */
export const COLLEGE_FOOTBALL_TEASER_ODDS_MAP = {
  2: { 6: -105, 6.5: -115, 7: -125, 8: -140, 9: -165 },
  3: { 6: 180, 6.5: 165, 7: 150, 8: 125, 9: 105, 10: -110, 11: -125 },
  4: { 6: 300, 6.5: 260, 7: 225, 8: 200, 9: 160, 10: 130, 11: 115, 12: 100, 13: -120 },
  5: { 6: 450, 6.5: 400, 7: 350, 8: 285, 9: 225, 10: 190, 11: 165, 12: 135, 13: 110 },
  6: { 6: 700, 6.5: 600, 7: 500, 8: 400, 9: 315, 10: 260, 11: 225, 12: 180, 13: 145 },
  7: { 6: 1100, 6.5: 850, 7: 750, 8: 550, 9: 425, 10: 350, 11: 300, 12: 240, 13: 180 },
  8: { 6: 1600, 6.5: 1200, 7: 1000, 8: 750, 9: 550, 10: 450, 11: 375, 12: 300, 13: 235 },
  9: { 6: 2200, 6.5: 1800, 7: 1400, 8: 1000, 9: 750, 10: 600, 11: 500, 12: 375, 13: 285 },
  10: { 6: 3000, 6.5: 2500, 7: 1800, 8: 1400, 9: 1000, 10: 750, 11: 600, 12: 450, 13: 350 },
  11: { 6.5: 3300, 7: 2500, 8: 1800, 9: 1200, 10: 1000, 11: 800, 12: 575, 13: 425 },
  12: { 7: 3300, 8: 2500, 9: 1600, 10: 1200, 11: 1000, 12: 700, 13: 500 },
};

/**
 * Pro + college basketball share one chart (client xlsx).
 * Standard 4–6 pts; extended 7–13 pts unlock with higher minimum team counts.
 */
export const BASKETBALL_TEASER_ODDS_MAP = {
  2: { 4: 100, 4.5: -110, 5: -120 },
  3: { 4: 180, 4.5: 160, 5: 150, 6: 125, 7: 100, 8: -110 },
  4: { 4: 300, 4.5: 260, 5: 225, 6: 190, 7: 150, 8: 130, 9: 100, 10: -120 },
  5: { 4: 500, 4.5: 400, 5: 350, 6: 300, 7: 220, 8: 180, 9: 140, 10: 115, 11: -110 },
  6: { 4: 700, 4.5: 600, 5: 500, 6: 425, 7: 300, 8: 250, 9: 190, 10: 150, 11: 110, 12: -130 },
  7: { 4: 1000, 4.5: 900, 5: 800, 6: 600, 7: 400, 8: 325, 9: 250, 10: 185, 11: 130, 12: -110, 13: -150 },
  8: { 4: 1500, 4.5: 1200, 5: 1000, 6: 800, 7: 500, 8: 400, 9: 300, 10: 225, 11: 160, 12: 115, 13: -125 },
  9: { 4: 2000, 4.5: 1800, 5: 1400, 6: 1000, 7: 700, 8: 550, 9: 400, 10: 300, 11: 200, 12: 135, 13: -105 },
  10: { 4: 3000, 4.5: 2500, 5: 2000, 6: 1500, 7: 1000, 8: 750, 9: 500, 10: 375, 11: 250, 12: 160, 13: 115 },
  11: { 7: 1200, 8: 1000, 9: 600, 10: 450, 11: 300, 12: 185, 13: 125 },
  12: { 7: 1600, 8: 1200, 9: 700, 10: 525, 11: 375, 12: 210, 13: 140 },
};

export const NBA_TEASER_ODDS_MAP = BASKETBALL_TEASER_ODDS_MAP;
export const COLLEGE_BASKETBALL_TEASER_ODDS_MAP = BASKETBALL_TEASER_ODDS_MAP;

export const TEASER_ODDS_BY_CHART = {
  nfl: NFL_TEASER_ODDS_MAP,
  collegeFootball: COLLEGE_FOOTBALL_TEASER_ODDS_MAP,
  nba: NBA_TEASER_ODDS_MAP,
  collegeBasketball: COLLEGE_BASKETBALL_TEASER_ODDS_MAP,
};

const normalize = (value) => String(value || "").trim().toLowerCase();

/** Issue #6 — detect college football from API keys or display titles. */
function isCollegeFootballSport(sportKey, sportTitle) {
  return (
    sportKey.includes("americanfootball_ncaaf") ||
    sportKey.includes("ncaaf") ||
    sportKey.includes("_fbs") ||
    sportKey.includes("_fcs") ||
    sportKey.includes("_cfb") ||
    sportTitle.includes("ncaaf") ||
    sportTitle.includes("college football") ||
    sportTitle.includes("cfb") ||
    sportTitle.includes("fbs") ||
    sportTitle.includes("fcs") ||
    (sportTitle.includes("ncaa") && sportTitle.includes("football")) ||
    (sportTitle.includes("college") &&
      !sportTitle.includes("basketball") &&
      (sportTitle.includes("football") || sportKey.includes("americanfootball")))
  );
}

/** Pro / NFL football — checked after college so NCAAF is never misclassified. */
function isProFootballSport(sportKey, sportTitle) {
  return (
    sportKey.includes("americanfootball_nfl") ||
    (sportKey.includes("nfl") && !sportKey.includes("ncaaf")) ||
    sportTitle === "nfl" ||
    /\bnfl\b/.test(sportTitle)
  );
}

function isCollegeBasketballSport(sportKey, sportTitle) {
  return (
    sportKey.includes("basketball_ncaab") ||
    sportKey.includes("ncaab") ||
    sportTitle.includes("college basketball") ||
    sportTitle.includes("ncaab") ||
    sportTitle.includes("ncaa basketball") ||
    (sportTitle.includes("college") && sportTitle.includes("basketball"))
  );
}

function isProBasketballSport(sportKey, sportTitle) {
  return (
    sportKey.includes("basketball_nba") ||
    (sportKey.includes("nba") && !sportKey.includes("ncaab")) ||
    /\bnba\b/.test(sportTitle)
  );
}

/**
 * Resolve which teaser pay chart applies to a single leg.
 * Supports sport_key / keyName (live slip + stored bets) and sport_title / title.
 * @returns {TeaserChartKey | null}
 */
export function resolveTeaserChartKey(leg = {}) {
  const sportKey = normalize(leg.sport_key || leg.keyName);
  const sportTitle = normalize(leg.sport_title || leg.title);

  // College football BEFORE pro — fixes issue #6 (college legs using NFL chart).
  if (isCollegeFootballSport(sportKey, sportTitle)) {
    return "collegeFootball";
  }

  if (isProFootballSport(sportKey, sportTitle)) {
    return "nfl";
  }

  if (isCollegeBasketballSport(sportKey, sportTitle)) {
    return "collegeBasketball";
  }

  if (isProBasketballSport(sportKey, sportTitle)) {
    return "nba";
  }

  // Generic football key — use title to split college vs pro instead of defaulting NFL.
  if (sportKey.includes("americanfootball")) {
    return isCollegeFootballSport(sportKey, sportTitle)
      ? "collegeFootball"
      : "nfl";
  }

  if (sportKey.includes("basketball")) {
    return isCollegeBasketballSport(sportKey, sportTitle)
      ? "collegeBasketball"
      : "nba";
  }

  return null;
}

const teaserUnsupportedSportMessage = () =>
  isDTS()
    ? "+Points Combo is only available for NFL, College Football, NBA, and College Basketball."
    : "Teaser is only available for NFL, College Football, NBA, and College Basketball.";

const teaserEmptySelectionMessage = () =>
  isDTS()
    ? "Add at least one +Points Combo selection."
    : "Add at least one teaser selection.";

const teaserMixedChartMessage = () =>
  isDTS()
    ? "All +Points Combo legs must use the same sport chart (e.g. all NFL or all College Football)."
    : "All teaser legs must use the same sport chart (e.g. all NFL or all College Football).";

/** All legs must share one chart; returns chart key or mixed-sport error. */
export function resolveTeaserChartForLegs(legs = []) {
  const list = Array.isArray(legs) ? legs.filter(Boolean) : [];
  if (list.length === 0) {
    return { ok: false, message: teaserEmptySelectionMessage() };
  }

  const keys = list.map((leg) => resolveTeaserChartKey(leg));
  const unknown = keys.findIndex((k) => !k);
  if (unknown !== -1) {
    return {
      ok: false,
      message: teaserUnsupportedSportMessage(),
    };
  }

  const unique = [...new Set(keys)];
  if (unique.length > 1) {
    return {
      ok: false,
      message: teaserMixedChartMessage(),
    };
  }

  return { ok: true, chartKey: unique[0] };
}

const TEASER_UNSUPPORTED_SPORT_MESSAGE = () => teaserUnsupportedSportMessage();

const teaserH2hRemovedMessage = () =>
  isDTS()
    ? "To Win picks were removed from your slip — they can't be part of a +Points Combo."
    : "Moneyline (M-Line) picks were removed from your slip — they can't be part of a teaser.";

/**
 * Normalize an existing slip when switching to teaser: drop h2h and legs that
 * cannot share one teaser chart (unsupported sport or mixed charts).
 * @returns {{ legs: object[], changed: boolean, message: string | null }}
 */
export function sanitizeLegsForTeaser(legs = []) {
  const list = Array.isArray(legs) ? legs.filter(Boolean) : [];
  if (!list.length) {
    return { legs: [], changed: false, message: null };
  }

  let message = null;
  const hadH2h = list.some((leg) => leg?.type === "h2h");
  let working = list.filter((leg) => leg?.type !== "h2h");

  if (hadH2h) {
    message = teaserH2hRemovedMessage();
  }

  if (!working.length) {
    return { legs: [], changed: true, message };
  }

  const chartCheck = resolveTeaserChartForLegs(working);
  if (chartCheck.ok) {
    return {
      legs: working,
      changed: working.length !== list.length,
      message,
    };
  }

  const supportedOnly = working.filter((leg) => resolveTeaserChartKey(leg));
  if (!supportedOnly.length) {
    return {
      legs: [],
      changed: true,
      message: chartCheck.message || TEASER_UNSUPPORTED_SPORT_MESSAGE(),
    };
  }

  const recheck = resolveTeaserChartForLegs(supportedOnly);
  if (!recheck.ok) {
    return {
      legs: [],
      changed: true,
      message: recheck.message,
    };
  }

  if (supportedOnly.length < working.length) {
    return {
      legs: supportedOnly,
      changed: true,
      message: chartCheck.message || TEASER_UNSUPPORTED_SPORT_MESSAGE(),
    };
  }

  return {
    legs: [],
    changed: true,
    message: chartCheck.message,
  };
}

export function getTeaserOddsMap(chartKey = "nfl") {
  return TEASER_ODDS_BY_CHART[chartKey] || NFL_TEASER_ODDS_MAP;
}

/**
 * Allowed teaser points for a chart + team count.
 * Higher point values (7+ basketball, 10+ football) only appear when the chart
 * defines them for that team count — per client Wager Examples.xlsx rules.
 */
export function getAllowedTeaserPoints(chartKey, teamCount) {
  const map = getTeaserOddsMap(chartKey);
  const row = map[teamCount];
  if (!row) return [];
  return Object.keys(row)
    .map(Number)
    .sort((a, b) => a - b);
}

export function isValidTeaserPoints(chartKey, teamCount, buyPoints) {
  const pts = Number(buyPoints);
  if (!Number.isFinite(pts)) return false;
  return getAllowedTeaserPoints(chartKey, teamCount).includes(pts);
}

export function getTeaserAmericanOdds(chartKey, teamCount, buyPoints) {
  const map = getTeaserOddsMap(chartKey);
  const odds = map[teamCount]?.[buyPoints];
  return odds === undefined || odds === null ? null : odds;
}

export function americanOddsToMultiplier(odds) {
  const o = Number(odds);
  if (!Number.isFinite(o) || o === 0) return 1;
  return o > 0 ? o / 100 + 1 : 100 / Math.abs(o) + 1;
}

export function calculateTeaserPayout(
  betAmount,
  teamCount,
  buyPoints = 6,
  chartKey = "nfl"
) {
  const bet = Number(betAmount);
  if (!Number.isFinite(bet) || bet <= 0 || !teamCount) return 0;

  const odds = getTeaserAmericanOdds(chartKey, teamCount, buyPoints);
  if (odds === null) return 0;

  return bet * americanOddsToMultiplier(odds);
}

export function formatTeaserChartLabel(chartKey) {
  return TEASER_CHART_LABELS[chartKey] || "Teaser";
}

export function formatTeaserAmericanOddsDisplay(odds) {
  if (odds === null || odds === undefined) return "-";
  return odds > 0 ? `+${odds}` : `${odds}`;
}

/** Move signed spread toward bettor favor (e.g. -7 + 6 → -1, +7 + 6 → +13). */
export function adjustTeaserSpreadPoint(point, teaserPoints) {
  const p = Number(point);
  const t = Number(teaserPoints);
  if (!Number.isFinite(p) || !Number.isFinite(t)) return point;
  return p + t;
}

/** Over total goes down; under total goes up. */
export function adjustTeaserTotalPoint(point, teamName, teaserPoints) {
  const p = Number(point);
  const t = Number(teaserPoints);
  if (!Number.isFinite(p) || !Number.isFinite(t)) return point;
  const isOver = String(teamName || "").toLowerCase() === "over";
  return isOver ? p - t : p + t;
}

export function adjustTeaserLine(bet = {}, teaserPointsOverride) {
  const teaserPoints = Number(
    teaserPointsOverride ?? bet.buyPoints ?? bet.teaserPoints ?? 6
  );
  const point = Number(bet.point);
  if (!Number.isFinite(point) || !Number.isFinite(teaserPoints)) {
    return bet.point;
  }

  const marketType = bet.type || bet.oddType;
  if (marketType === "spreads") {
    return adjustTeaserSpreadPoint(point, teaserPoints);
  }
  if (marketType === "totals") {
    return adjustTeaserTotalPoint(
      point,
      bet.pointType || bet.teamName,
      teaserPoints
    );
  }
  return bet.point;
}
